import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { usePlanFeatures } from '../../hooks/usePlanFeatures';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { slideUp } from '@/lib/motion';
import PageHeader from '@/components/common/PageHeader';
import CalendarToolbar, {
  type CalendarView,
} from '../../components/calendar/CalendarToolbar';
import CalendarMonthView from '../../components/calendar/CalendarMonthView';
import CalendarWeekView from '../../components/calendar/CalendarWeekView';
import CalendarDayView from '../../components/calendar/CalendarDayView';
import CalendarAgendaView from '../../components/calendar/CalendarAgendaView';
import ConnectGoogleEmptyState from '../../components/calendar/ConnectGoogleEmptyState';
import ReconnectBanner from '../../components/calendar/ReconnectBanner';
import LearnerEventDetailModal from '../../components/learner/calendar/LearnerEventDetailModal';
import {
  useLearnerIntegrations,
  useStartLearnerGoogleConnect,
} from '../../hooks/learner/useLearnerIntegrations';
import { useLearnerCalendarEvents } from '../../hooks/learner/useLearnerCalendarEvents';
import {
  getViewRange,
  navigateCursor,
} from '../../components/calendar/calendarDateUtils';
import type { CalendarEvent } from '../../types/integrations';

const ERROR_REASON_COPY: Record<string, string> = {
  invalid_state: 'Connection failed — please try again.',
  user_mismatch:
    'You appear to have switched accounts. Sign back in and try again.',
  token_exchange_failed: 'Google rejected the connection. Please try again.',
  missing_account_email: 'Could not read your Google account. Please try again.',
  access_denied: 'Connection was canceled.',
};

const LEARNER_SETTINGS_HREF = '/learn/settings?tab=integrations';

const LearnerCalendarPage: React.FC = () => {
  const { calendar: calendarAllowed } = usePlanFeatures();
  const [view, setView] = useState<CalendarView>('month');
  const [cursorDate, setCursorDate] = useState<Date>(new Date());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const status = searchParams.get('status');
    if (!status) return;
    const reason = searchParams.get('reason');
    const accountEmail = searchParams.get('account_email');
    if (status === 'success') {
      toast.success(
        accountEmail
          ? `Google Calendar connected — ${accountEmail}`
          : 'Google Calendar connected'
      );
    } else if (status === 'error') {
      toast.error(
        ERROR_REASON_COPY[reason ?? ''] || 'Connection failed — please try again.'
      );
    }
    const next = new URLSearchParams(searchParams);
    next.delete('status');
    next.delete('provider');
    next.delete('account_email');
    next.delete('reason');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const { data: connections = [], isLoading: connectionsLoading } =
    useLearnerIntegrations();
  const startConnect = useStartLearnerGoogleConnect();
  const googleConnection = useMemo(
    () => connections.find((c) => c.provider === 'google') ?? null,
    [connections]
  );

  const isConnected = googleConnection?.status === 'active';
  const needsReauth =
    !!googleConnection &&
    (googleConnection.status === 'reauth_required' ||
      googleConnection.status === 'expired' ||
      googleConnection.status === 'revoked');

  const { from, to } = useMemo(
    () => getViewRange(view, cursorDate),
    [view, cursorDate]
  );
  const { data: events = [], isLoading: eventsLoading } = useLearnerCalendarEvents({
    from,
    to,
    enabled: isConnected,
  });

  const handleNavigate = (delta: -1 | 0 | 1) => {
    setCursorDate((prev) => navigateCursor(view, prev, delta));
  };

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
  };

  const renderView = () => {
    if (!isConnected) return null;
    const props = { events, onEventClick: handleEventClick };
    switch (view) {
      case 'month':
        return <CalendarMonthView cursorDate={cursorDate} {...props} />;
      case 'week':
        return <CalendarWeekView cursorDate={cursorDate} {...props} />;
      case 'day':
        return <CalendarDayView cursorDate={cursorDate} {...props} />;
      case 'agenda':
        return (
          <CalendarAgendaView
            cursorDate={cursorDate}
            events={events}
            onEventClick={handleEventClick}
          />
        );
      default:
        return null;
    }
  };

  // Learner surface: plan-locked features vanish entirely — no upsell copy.
  if (!calendarAllowed) {
    return <Navigate to="/learn/dashboard" replace />;
  }

  return (
    <motion.div {...slideUp}>
      <PageHeader title="Calendar" root={{ label: 'Home', to: '/learn/dashboard' }} className="mb-6" />

      {connectionsLoading ? (
        <div className="card-elevated p-12 text-center text-text-secondary">
          Loading…
        </div>
      ) : !googleConnection ? (
        <ConnectGoogleEmptyState
          startConnect={startConnect}
          settingsHref={LEARNER_SETTINGS_HREF}
        />
      ) : (
        <div className="space-y-4">
          {needsReauth && (
            <ReconnectBanner
              status={googleConnection.status}
              startConnect={startConnect}
            />
          )}
          <div
            className={[
              'card-elevated overflow-hidden',
              needsReauth ? 'opacity-60 pointer-events-none' : '',
            ].join(' ')}
          >
            <CalendarToolbar
              view={view}
              onViewChange={setView}
              cursorDate={cursorDate}
              onNavigate={handleNavigate}
              onTodayClick={() => setCursorDate(new Date())}
              disabled={needsReauth}
            />
            <div className="relative min-h-[600px]">
              {eventsLoading && (
                <div className="absolute top-2 right-2 text-xs text-text-tertiary">
                  Refreshing…
                </div>
              )}
              {renderView()}
            </div>
          </div>
        </div>
      )}

      {selectedEvent && (
        <LearnerEventDetailModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </motion.div>
  );
};

export default LearnerCalendarPage;
